import React from 'react';
import { useFormikContext } from 'formik';

import Text from '~/components/core/TextComponents/Text';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { MultiSelectTextFieldFormik } from '~/components/TextFieldFormik';
import { getLobDescription } from '~/Utils/lobUtils';

import type { GlobalFieldFormValues } from './contextScope';
import { ALL_SENTINEL } from './contextScope';

/**
 * The LOB and sub-organization context a global field is projected into.
 *
 * There is no shared picker to reuse: `CoverageDialog` and `SharedCoverageDialog` each build
 * their own from `MultiSelectTextFieldFormik` plus an `'all'` sentinel, and their LOB half is a
 * single-select radio. A global field covers *several* LOBs, so the structure does not transfer
 * — the convention does, and this follows it: `'all'` in the list means every member, including
 * ones added later, and the two are flattened to the backend's `is_all_*` / list pairs on submit.
 *
 * Local to this screen rather than promoted to `core/`: nothing else selects a multi-LOB scope
 * today, and a second caller can lift it when one exists.
 */
const GlobalFieldContextPicker: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore useSysconfig is an untyped JSX module.
  const { organization } = useSysconfig();
  const { lobConfigurationsDict } = useLobConfiguration();
  const { values } = useFormikContext<GlobalFieldFormValues>();

  const isAllLobs = values.lobs.includes(ALL_SENTINEL);
  const isAllSubOrgs = values.sub_organization_ids.includes(ALL_SENTINEL);
  // LOB keys (the union across sub-organizations when they are enabled). Not `supported_lobs`,
  // which holds one object per LOB; `useSysconfig` is untyped, so tsc cannot tell them apart.
  const supportedLobs: string[] = organization.supported_claim_types ?? [];
  const subOrganizations = organization.sub_organizations ?? [];
  const subOrgIdToName: Record<number, string> = Object.fromEntries(
    subOrganizations.map((subOrg: { id: number; name: string }) => [subOrg.id, subOrg.name])
  );

  // An explicit list is a snapshot, not a rule: the backend projects into exactly the members
  // named, so a LOB or sub-organization added afterwards is not covered until someone edits the
  // field. Said here rather than discovered when a new sub-org's forms come up missing the field.
  const explicitListNote =
    !isAllLobs || !isAllSubOrgs ? (
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        This field is limited to the lines of business and sub-organizations selected above. Ones added later are not
        included until this field is edited.
      </Text>
    ) : null;

  return (
    <div className="flex flex-col gap-12">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        Context
      </Text>
      <div className="grid grid-cols-2 gap-24">
        <MultiSelectTextFieldFormik
          id="lobs"
          label="Lines of Business"
          fullWidth
          // Collapsing to the sentinel alone once "All" is picked keeps the two states from
          // coexisting, so the submit mapping never has to decide which of them wins.
          options={isAllLobs ? [ALL_SENTINEL] : [ALL_SENTINEL, ...supportedLobs]}
          renderOption={(lob: string) =>
            lob === ALL_SENTINEL
              ? 'All (including future lines of business)'
              : (getLobDescription(lob, lobConfigurationsDict) ?? lob)
          }
          withOptionChips
        />
        {organization.sub_organizations_enabled ? (
          <MultiSelectTextFieldFormik
            id="sub_organization_ids"
            label="Sub Organizations"
            fullWidth
            options={
              isAllSubOrgs
                ? [ALL_SENTINEL]
                : [ALL_SENTINEL, ...subOrganizations.map((subOrg: { id: number }) => subOrg.id)]
            }
            renderOption={(subOrgId: string | number) =>
              subOrgId === ALL_SENTINEL
                ? 'All (including future sub-organizations)'
                : subOrgIdToName[subOrgId as number]
            }
            withOptionChips
          />
        ) : null}
      </div>
      {explicitListNote}
    </div>
  );
};

export default GlobalFieldContextPicker;
